import type { FC, ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { SliderInput } from '@sravni/react-design-system';

import { AMOUNT_SETTINGS } from '@src/constants/filters';
import { sendChangeAmountFilterEvent } from '@src/helpers/analyticsEvents';
import { validateByParams } from '@src/helpers/validate';

type Steps = Array<{ ceilThreshold: number; step: number }>;

interface IProps {
    className?: string;
    value?: number | null;
    name: string;
    label?: string;
    placeholder?: string;
    max?: number;
    error?: string;
    onChange: (value: number | undefined, name: string) => void;
    format?: string | ((value: string) => string);
    postfix?: string;
    defaultValue?: number;
    icon?: ReactNode;
    autoFocus?: boolean;
    steps: Steps;
}

const THOUSAND_SEPARATOR = ' ';

export const AmountControlV2: FC<IProps> = ({
    value,
    name,
    label,
    max = 100000,
    placeholder,
    onChange,
    autoFocus,
    icon,
    postfix,
    defaultValue,
    className,
    error,
    steps,
    ...props
}) => {
    const validate = useMemo(() => validateByParams({ max }), [max]);

    const step = useMemo(() => getStep(value || 0, steps), [value, steps]);

    const validatedValue = validate(value, step);

    const handleChange = useRef(onChange);

    useEffect(() => {
        // валидация: если в query-параметрах кто-то вручную исправит значение
        if (validatedValue !== value) {
            handleChange.current(validatedValue, name);
        }
    }, [name, validatedValue, value]);

    const handleChangeAmountInput = useCallback(
        (value = 0) => {
            if (value < steps[0]?.step) return;
            const updatedStep = getStep(value, steps);
            const valueAfterValidating = validate(value, updatedStep);

            debounce(() => sendChangeAmountFilterEvent(valueAfterValidating || 0), 600)();

            handleChange.current(valueAfterValidating, name);
        },
        [name, validate, steps],
    );

    return (
        <SliderInput
            label={error || label}
            className={className}
            placeholder={placeholder}
            value={validatedValue}
            postfix={postfix}
            min={0}
            max={max}
            defaultValue={defaultValue}
            icon={icon}
            autoFocus={autoFocus}
            invalid={Boolean(error)}
            thousandSeparator={THOUSAND_SEPARATOR}
            inputMode="numeric"
            onChange={handleChangeAmountInput}
            step={step}
            maxLength={AMOUNT_SETTINGS.maxLength}
            {...props}
        />
    );
};

function getStep(amount: number, steps: Steps, fallbackStep = 1000) {
    const appropriateObj = steps.find(({ ceilThreshold }) => amount < ceilThreshold) ?? steps[steps.length - 1];

    return appropriateObj?.step ?? fallbackStep;
}

function debounce<F extends (...params: any[]) => void>(fn: F, delay: number) {
    let timeoutID: number | null = null;
    return function (this: any, ...args: any[]) {
        timeoutID && clearTimeout(timeoutID);
        timeoutID = window.setTimeout(() => fn.apply(this, args), delay);
    } as F;
}
